import styled from "styled-components";
import { breakpoint, theme } from "@utilities";
import CopyButton from "src/Module/CopyButton";
import { selectPoints, useAppSelector } from "@redux";
import moment from "moment";
import { useEffect, useState } from "react";
import { PATH_COMMISSION_LEARN_MORE } from "@routes";
import { useNavigate } from "react-router-dom";
import { MonthSelector } from "src/Module/Calendar/MonthSelector";

export const CommissionsTab = ({
    onShowAllChange,
    showAllProp,
}: {
    onShowAllChange: (show: boolean) => void;
    showAllProp?: boolean;
}) => {
    const { commissionHistory } = useAppSelector(selectPoints);
    const [showAll, setShowAll] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState<number>(moment().month());
    const [selectedYear, setSelectedYear] = useState<number>(moment().year());
    const [showHelpPopup, setShowHelpPopup] = useState(false);
    const [selectedCommission, setSelectedCommission] = useState<any>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showDetailsHelp, setShowDetailsHelp] = useState(false);

    const navigate = useNavigate();

    // Prevent body scroll when modals are open
    useEffect(() => {
        if (showHelpPopup || showDetailsModal || showDetailsHelp) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [showHelpPopup, showDetailsModal, showDetailsHelp]);

    // Sync internal state with prop
    if (showAllProp !== undefined && showAllProp !== showAll) {
        setShowAll(showAllProp);
    }

    const handleToggleShowAll = () => {
        const newShowAll = !showAll;
        setShowAll(newShowAll);
        onShowAllChange(newShowAll);
    };

    // Get current week's Tuesday boundaries
    const today = moment();
    const currentDayOfWeek = today.day(); // 0 = Sunday, 2 = Tuesday

    // Calculate last Tuesday (start of current week)
    const lastTuesday = today.clone().day(currentDayOfWeek >= 2 ? 2 : -5);

    // Calculate next Tuesday (end of current week)
    const nextTuesday = lastTuesday.clone().add(7, "days");

    // Filter commissions for current week
    const currentWeekCommissions = (commissionHistory || []).filter((item) => {
        const purchaseDate = moment(item.datePurchased);
        return (
            purchaseDate.isSameOrAfter(lastTuesday, "day") &&
            purchaseDate.isBefore(nextTuesday, "day")
        );
    });

    // Calculate available this week (sum of commissions with availableDate <= today)
    const availableThisWeek = currentWeekCommissions
        .filter((item) => moment(item.availableDate).isSameOrBefore(today, "day"))
        .reduce((sum, item) => sum + parseFloat(item.commission), 0)
        .toFixed(2);

    // Calculate pending (sum of all commissions with availableDate > today, not just current week)
    const pending = (commissionHistory || [])
        .filter((item) => moment(item.availableDate).isAfter(today, "day"))
        .reduce((sum, item) => sum + parseFloat(item.commission), 0)
        .toFixed(2);

    const totalCommission = (parseFloat(availableThisWeek) + parseFloat(pending)).toFixed(2);

    // Filter commissions by selected month/year when in showAll mode
    const filteredCommissionHistory = showAll
        ? (commissionHistory || []).filter((item) => {
              const purchaseDate = moment(item.datePurchased);
              return purchaseDate.month() === selectedMonth && purchaseDate.year() === selectedYear;
          })
        : commissionHistory || [];

    return (
        <CommissionsView>
            {!showAll && (
                <>
                    <EarningsBalanceSection>
                        <SectionHeader>
                            <h2>Your current balance</h2>
                            <HelpIcon onClick={() => setShowHelpPopup(true)}>?</HelpIcon>
                        </SectionHeader>
                        <EarningsBalanceCards>
                            <EarningsBalanceCard $type="available">
                                <p>Available This week</p>
                                <h3>${availableThisWeek}</h3>
                            </EarningsBalanceCard>
                            <EarningsBalanceCard $type="pending">
                                <p>
                                    Pending <span>›</span>
                                </p>
                                <h3>${pending}</h3>
                            </EarningsBalanceCard>
                        </EarningsBalanceCards>
                        <PointsBalanceText>Total Balance: ${totalCommission}</PointsBalanceText>
                    </EarningsBalanceSection>
                </>
            )}
            <SectionHeader $showAll={showAll}>
                <h2>Commission History</h2>
                {showAll ? (
                    <MonthSelectorWrapper>
                        <MonthSelector
                            month={selectedMonth}
                            year={selectedYear}
                            onMonthYearChange={(month, year) => {
                                setSelectedMonth(month);
                                setSelectedYear(year);
                            }}
                        />
                    </MonthSelectorWrapper>
                ) : (
                    <ViewAllLink onClick={handleToggleShowAll}>View All</ViewAllLink>
                )}
            </SectionHeader>
            <HistorySection>
                {(showAll ? filteredCommissionHistory : (commissionHistory || []).slice(0, 4))
                    .length === 0 ? (
                    <EmptyState>
                        <p>You don't have any History for this month</p>
                    </EmptyState>
                ) : (
                    <TransactionList>
                        {(showAll
                            ? filteredCommissionHistory
                            : (commissionHistory || []).slice(0, 4)
                        ).map((transaction, index) => {
                            const isPending = moment(transaction.availableDate).isAfter(
                                today,
                                "day",
                            );
                            return (
                                <TransactionItem key={index}>
                                    <TransactionLeft>
                                        <TransactionId>
                                            {transaction.bookingReference}
                                            <CopyButton value={transaction.bookingReference} />
                                        </TransactionId>
                                        <TransactionDate>
                                            Available from{" "}
                                            {moment(transaction.availableDate).format(
                                                "MMM DD, YYYY",
                                            )}
                                        </TransactionDate>
                                        <ViewDetailsLink
                                            onClick={() => {
                                                setSelectedCommission(transaction);
                                                setShowDetailsModal(true);
                                            }}
                                        >
                                            View Details
                                        </ViewDetailsLink>
                                    </TransactionLeft>
                                    <TransactionRight>
                                        <TransactionAmount $positive={true}>
                                            +${transaction.commission}
                                        </TransactionAmount>
                                        {isPending && <PendingBadge>Pending</PendingBadge>}
                                    </TransactionRight>
                                </TransactionItem>
                            );
                        })}
                    </TransactionList>
                )}
            </HistorySection>

            {!showAll && (
                <InfoCard onClick={() => navigate(PATH_COMMISSION_LEARN_MORE)}>
                    <img src="/images/Currency.svg" alt="currency" />
                    <InfoContent>
                        <h4>How Commission work?</h4>
                        <p>Commission rates, payout schedule, FAQs and terms</p>
                    </InfoContent>
                    <img src="/images/ChevronVectorRight.svg" alt="chevron" />
                </InfoCard>
            )}
            {showHelpPopup && (
                <PopupOverlay onClick={() => setShowHelpPopup(false)}>
                    <PopupContent onClick={(e) => e.stopPropagation()}>
                        <PopupHeader>
                            <h3>Commission Information</h3>
                        </PopupHeader>
                        <PopupBody>
                            <p>
                                Available commissions are commissions that are ready to be paid to
                                your registered bank account. Total commissions include both
                                commissions that are pending and those that will be paid after the
                                travel date has passed.
                            </p>
                            <p>
                                Pending commissions become available once the travel date has passed
                                and are then processed for bank payout.
                            </p>
                        </PopupBody>
                        <PopupFooter>
                            <GotItButton onClick={() => setShowHelpPopup(false)}>
                                Got it
                            </GotItButton>
                        </PopupFooter>
                    </PopupContent>
                </PopupOverlay>
            )}
            {showDetailsModal && selectedCommission && (
                <DetailsOverlay onClick={() => setShowDetailsModal(false)}>
                    <DetailsContent onClick={(e) => e.stopPropagation()}>
                        <DetailsCard>
                            <CardHeader>
                                <OrderIdSection>
                                    <OrderId>
                                        #{selectedCommission.bookingReference}
                                        <CopyButton value={selectedCommission.bookingReference} />
                                    </OrderId>
                                    <CreatedDate>
                                        Available From{" "}
                                        {moment(selectedCommission.availableDate).format(
                                            "MMM DD, YYYY",
                                        )}
                                    </CreatedDate>
                                </OrderIdSection>
                                <EarningSummaryTitle>
                                    Earning summary
                                    <HelpIcon onClick={() => setShowDetailsHelp(true)}>?</HelpIcon>
                                </EarningSummaryTitle>
                            </CardHeader>
                            <CardBody>
                                <FinancialRow>
                                    <RowLabel>Total Commission</RowLabel>
                                    <RowValue>${selectedCommission.commission}</RowValue>
                                </FinancialRow>
                                <NetCommissionRow>
                                    <NetLabel>Earned from this order</NetLabel>
                                    <NetValueSection>
                                        {moment(selectedCommission.availableDate).isAfter(
                                            moment(),
                                            "day",
                                        ) && <PendingBadge>Pending</PendingBadge>}
                                        <NetAmount>${selectedCommission.commission}</NetAmount>
                                    </NetValueSection>
                                </NetCommissionRow>
                            </CardBody>
                            <CardFooter>
                                Booked on{" "}
                                {moment(selectedCommission.datePurchased).format("MMM DD, YYYY")}
                            </CardFooter>
                        </DetailsCard>
                    </DetailsContent>
                </DetailsOverlay>
            )}
            {showDetailsHelp && (
                <PopupOverlay onClick={() => setShowDetailsHelp(false)}>
                    <PopupContent onClick={(e) => e.stopPropagation()}>
                        <PopupHeader>
                            <h3>Earning Summary</h3>
                        </PopupHeader>
                        <PopupBody>
                            <p>
                                Total Commission is the full commission amount earned from this
                                booking. Earned Commission is the amount after any deductions, which
                                will be paid to your account.
                            </p>
                        </PopupBody>
                        <PopupFooter>
                            <GotItButton onClick={() => setShowDetailsHelp(false)}>
                                Got it
                            </GotItButton>
                        </PopupFooter>
                    </PopupContent>
                </PopupOverlay>
            )}
        </CommissionsView>
    );
};

const CommissionsView = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const EarningsBalanceSection = styled.div`
    background: white;
    padding: 24px;
    border-radius: 0px 0px 12px 12px;
`;

const SectionHeader = styled.div<{ $showAll?: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: ${({ $showAll }) => ($showAll ? "24px" : "0")};

    h2 {
        font-size: 18px;
        font-weight: 600;
        margin: 0;
    }
`;

const MonthSelectorWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const HelpIcon = styled.div`
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 2px solid ${theme.colors.highlight};
    color: ${theme.colors.highlight};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
`;

const EarningsBalanceCards = styled.div`
    margin-top: 24px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
`;

const EarningsBalanceCard = styled.div<{ $type: "available" | "pending" }>`
    background: ${({ $type }) => ($type === "available" ? "#E8F9F4" : "#FFF9E6")};
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    border: 1px solid ${({ $type }) => ($type === "available" ? "#00BFA5" : "#FFA500")};

    p {
        font-size: 14px;
        color: #131214;
        margin: 0 0 12px 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;

        span {
            font-size: 18px;
        }
    }

    h3 {
        font-size: 32px;
        font-weight: 700;
        margin: 0;
        color: ${({ $type }) => ($type === "available" ? "#00BFA5" : "#FFA500")};
    }

    ${breakpoint.breakOnlyMobile`
        padding: 16px;
        height: 76px;
        display: flex;
        flex-direction: column;
        justify-content: center;

        p {
            font-size: 12px;
            margin: 0 0 4px 0;

            span {
                font-size: 14px;
            }
        }

        h3 {
            font-size: 24px;
            font-weight: 600;
        }
    `}
`;

const PointsBalanceText = styled.div`
    font-size: 12px;
    color: #6e7375;
    margin-top: 12px;
    text-align: right;
`;

const HistorySection = styled.div`
    background: white;
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
`;

const ViewAllLink = styled.a`
    font-size: 14px;
    color: ${theme.colors.highlight};
    cursor: pointer;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const TransactionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const TransactionItem = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 16px;
    border-radius: 8px;
    border: 1px solid #e6e9eb;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #00bfa5;
        background: #f0fffe;
    }
`;

const TransactionLeft = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const TransactionId = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #131214;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const TransactionDate = styled.div`
    font-size: 12px;
    color: #6e7375;
`;

const ViewDetailsLink = styled.a`
    font-size: 12px;
    color: ${theme.colors.highlight};
    cursor: pointer;
    text-decoration: underline;
`;

const TransactionRight = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
`;

const TransactionAmount = styled.div<{ $positive?: boolean }>`
    font-size: 16px;
    font-weight: 600;
    color: ${({ $positive }) => ($positive ? "#00BFA5" : "#131214")};
`;

const PendingBadge = styled.div`
    font-size: 12px;
    color: #ffa500;
    background: #fff9e6;
    padding: 4px 8px;
    border-radius: 8px;
    font-weight: 500;
`;

const InfoCard = styled.div`
    background: white;
    border-radius: 12px;
    padding: 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const InfoContent = styled.div`
    flex: 1;

    h4 {
        font-size: 16px;
        font-weight: 600;
        margin: 0 0 4px 0;
        color: #131214;
    }

    p {
        font-size: 13px;
        color: #6e7375;
        margin: 0;
    }
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 80px 20px;
    color: #6e7375;
    font-size: 16px;
    gap: 14px;

    p {
        margin: 0;
        font-weight: 500;
    }
`;

const PopupOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const PopupContent = styled.div`
    background: white;
    border-radius: 12px;
    max-width: 500px;
    width: 90%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const PopupHeader = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e6e9eb;

    h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        color: #131214;
    }
`;

const PopupBody = styled.div`
    padding: 24px;
    text-align: center;

    p {
        margin: 0 0 16px 0;
        font-size: 14px;
        line-height: 1.6;
        color: #131214;

        &:last-child {
            margin-bottom: 0;
        }
    }
`;

const PopupFooter = styled.div`
    padding: 20px 24px;
    border-top: 1px solid #e6e9eb;
    display: flex;
    justify-content: center;
`;

const GotItButton = styled.button`
    background: linear-gradient(90deg, ${theme.gradients.button[0]}, ${theme.gradients.button[1]});
    color: white;
    border: none;
    border-radius: 8px;
    padding: 12px 32px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.9;
    }
`;

const DetailsOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    ${breakpoint.breakOnlyMobile`
        align-items: flex-end;
    `}
`;

const DetailsContent = styled.div`
    background: #f5f5f5;
    border-radius: 12px;
    max-width: 600px;
    width: 90%;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

    ${breakpoint.breakOnlyMobile`
        width: 100%;
        border-radius: 12px 12px 0 0;
        max-width: 100%;
    `}
`;

const DetailsCard = styled.div`
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const CardHeader = styled.div`
    padding: 24px 24px 16px 24px;
    display: flex;
    flex-direction: column;
`;

const OrderIdSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const OrderId = styled.div`
    font-size: 18px;
    font-weight: 700;
    color: #333333;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CreatedDate = styled.div`
    font-size: 12px;
    color: #666666;
`;

const EarningSummaryTitle = styled.div`
    margin-top: 8px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 18px;
    font-family: "poppins", sans-serif;
    font-weight: 500;
    gap: 8px;
    color: #333333;
`;

const CardBody = styled.div`
    padding: 0 24px;
    display: flex;
    flex-direction: column;
`;

const FinancialRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
`;

const RowLabel = styled.span`
    font-size: 14px;
    color: #666666;
    font-weight: 400;
`;

const RowValue = styled.span`
    font-size: 14px;
    color: #666666;
    font-weight: 400;
`;

const NetCommissionRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 0;
    border-top: 1px solid #e6e9eb;
`;

const NetLabel = styled.span`
    font-size: 14px;
    color: #333333;
    font-weight: 700;
`;

const NetValueSection = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const NetAmount = styled.span`
    font-size: 18px;
    color: #10b981;
    font-weight: 700;
`;

const CardFooter = styled.div`
    padding: 16px 24px;
    text-align: end;
    font-size: 12px;
    color: #666666;
    font-style: italic;
    background: #fafafa;
    border-radius: 0 0 12px 12px;
`;
